<script lang="ts">
  import { differenceInDays, differenceInMonths, differenceInWeeks, differenceInYears } from 'date-fns';
  import { createEventDispatcher } from 'svelte';
  import { Writable, get } from 'svelte/store';
  import { getStr, isDirectionRTL, roundToAtMaxNDigits } from '../../../common/util';
  import Icon from '../../common/components/Icon.svelte';
  import Text from '../../common/components/Text.svelte';
  import {
    customerReviews,
    expertReviews,
    extraReviews,
    prodReviewFlag,
    productDetails,
    isReviewAvailable,
    pdpMoreInfoActiveTab,
    reviewNextPageUrls,
    reviewGalleryState,
  } from '../store/pdp_store';
  import PdpDivider from './PdpDivider.svelte';

  $productDetails = window['ACC'].config.productDetails;

  export let isGeekInfolink = false;
  export let testFreakData = null;
  export let isExpanded = false;

  const isRTL = isDirectionRTL();
  const dispatch = createEventDispatcher();

  let innerWidth = window.innerWidth;
  $: isMobileView = innerWidth < 900;
  $: isDesktopView = !isMobileView;

  let data = $customerReviews;
  $: customerReviewCount = testFreakData && testFreakData.user_review_count;
  $: expertReviewCount = testFreakData && testFreakData.pro_review_count;
  $: eXtraReviewCount = testFreakData && testFreakData.your_review_count;

  const CustomerReviewText = getStr('extra.pdp.customerReviews');
  const ExpertReviewText = getStr('extra.pdp.Expert_Reviews');
  const ExtraReviewText = getStr('extra.pdp.eXtra_Reviews');

  let showingOfReviewText = CustomerReviewText;
  let itemToShow = data.length;
  const defaultCharLn = 100;
  let TotalCount = customerReviewCount;
  let viewMore = true;
  let offSetCount = 0;
  let openPropsReadMore = false;
  let openConsReadMore = false;
  let propsExpanIndex = -1;
  let consExpanIndex = -1;
  let userReviewExpanIndex = -1;
  let isSelectedTab = 1;

  // Extract images from customer reviews (testFreakData or $customerReviews)
  function getReviewImages(item) {
    // Extract images from any review type (customer, expert, extra)
    // If the review has an images array, extract the 'medium' or 'icon' URLs
    if (item && Array.isArray(item.images) && item.images.length > 0) {
      // Prefer 'medium', fallback to 'icon', fallback to any available URL
      return item.images.map(img => img.medium || img.icon || img.large || '');
    }
    return [];
  }

  $: if (testFreakData && $prodReviewFlag) {
    (async () => {
      reviewNextPageUrls.set({
        user: '',
        pro: '',
        your: '',
      });
      await APICall('your');
      await APICall('pro');
      await APICall('user');
    })();
    TotalCount = customerReviewCount;
    viewMore = true;
    TotalCount = TotalCount ? TotalCount : 0;
    // Update data for default tab (Customer Reviews - tab 1)
    data = $customerReviews;
    itemToShow = data.length;
    offSetCount = data.length;
    if (offSetCount >= TotalCount) {
      viewMore = false;
    }
    $prodReviewFlag = false;
  }

  // Ensure reviews are displayed when tab becomes active on mobile
  $: if ($pdpMoreInfoActiveTab === 'Reviews' && isGeekInfolink) {
    // Make sure data is set to the correct review type based on selected tab
    if (isSelectedTab === 1) {
      data = $customerReviews;
    } else if (isSelectedTab === 2) {
      data = $expertReviews;
    } else {
      data = $extraReviews;
    }
    // Update itemToShow and offSetCount
    if (data.length > 0 && itemToShow === 0) {
      itemToShow = data.length;
      offSetCount = data.length;
    }
  }
  $: if ($customerReviews.length > 0 || $expertReviews.length > 0 || $extraReviews.length > 0) {
    isReviewAvailable.set(true);
  } else {
    isReviewAvailable.set(false);
  }

  const handleExpand = () => {
    isExpanded = !isExpanded;
  };

  const handleViewMore = async () => {
    switch (isSelectedTab) {
      case 1:
        {
          await APICall('user');
        }

        break;
      case 2:
        {
          await APICall('pro');
        }

        break;

      default:
        {
          await APICall('your');
        }
        break;
    }
    if (offSetCount >= TotalCount) {
      viewMore = false;
    }
  };

  async function APICall(type: string) {
    let reviewArrayStore: Writable<any[]>;
    let nextPageUrl: string;
    switch (type) {
      case 'your':
        reviewArrayStore = extraReviews;
        nextPageUrl = get(reviewArrayStore).length > 0 ? $reviewNextPageUrls.your : testFreakData?.your_review_url;
        break;

      case 'pro':
        reviewArrayStore = expertReviews;
        nextPageUrl = get(reviewArrayStore).length > 0 ? $reviewNextPageUrls.pro : testFreakData?.pro_review_url;
        break;

      default:
        reviewArrayStore = customerReviews;
        // Use the provided static test API URL for customer reviews if not already loaded
        nextPageUrl = get(reviewArrayStore).length > 0 ? $reviewNextPageUrls.user : testFreakData?.user_review_url;
    }

    if (nextPageUrl?.length > 0) {
      if (nextPageUrl.indexOf('limit') === -1) {
        nextPageUrl = `${nextPageUrl}&limit=6`;
      }
      if (nextPageUrl.indexOf('sort') === -1) {
        nextPageUrl = `${nextPageUrl}&sort=date`;
      }
      const response = await fetch(nextPageUrl);
      if (response.status === 200) {
        const responseJson = await response.json();
        let reviews = responseJson.reviews;
        reviews = reviews.map(r => {
          if (r.score_max === 10) {
            r.score_max = 5;
            r.score = roundToAtMaxNDigits(r.score / 2, 10);
          }
          return r;
        });
        const newNextPageUrl = 'next_page_url' in responseJson ? responseJson.next_page_url : '';
        reviewArrayStore.set([...get(reviewArrayStore), ...reviews]);

        // template related variables
        data = get(reviewArrayStore);
        itemToShow = data.length;
        offSetCount = itemToShow;

        reviewNextPageUrls.update(currentUrls => ({
          ...currentUrls,
          [type]: newNextPageUrl,
        }));
      }
    }
  }

  const handleTabSelection = i => {
    if (isSelectedTab == i) {
      return;
    }
    viewMore = false;
    isSelectedTab = i;
    switch (i) {
      case 1:
        {
          showingOfReviewText = CustomerReviewText;
          TotalCount = customerReviewCount;
          data = $customerReviews;
          offSetCount = data.length;
          itemToShow = data.length;
        }
        break;
      case 2:
        {
          showingOfReviewText = ExpertReviewText;
          TotalCount = expertReviewCount;
          data = $expertReviews;
          offSetCount = data.length;
          itemToShow = data.length;
        }
        break;
      default:
        {
          showingOfReviewText = ExtraReviewText;
          TotalCount = eXtraReviewCount;
          data = $extraReviews;
          offSetCount = data.length;
          itemToShow = data.length;
        }
        break;
    }
    if (itemToShow == undefined) {
      itemToShow = 0;
    }
    if (TotalCount == undefined) {
      TotalCount = 0;
    }
    if (offSetCount < TotalCount) {
      viewMore = true;
    }
  };

  const propsReadMore = i => {
    if (propsExpanIndex !== i) {
      propsExpanIndex = i;
      openPropsReadMore = true;
    } else {
      propsExpanIndex = -1;
      openPropsReadMore = false;
    }
  };
  const consReadMore = i => {
    if (consExpanIndex !== i) {
      consExpanIndex = i;
      openConsReadMore = true;
    } else {
      consExpanIndex = -1;
      openConsReadMore = true;
    }
  };
  const userReviewReadMore = i => {
    if (userReviewExpanIndex !== i) {
      userReviewExpanIndex = i;
    } else {
      userReviewExpanIndex = -1;
    }
  };

  const reviewdurationPeriod = reviewDate => {
    const daysDifference = differenceInDays(Date.now(), new Date(reviewDate));
    const weeksDifference = differenceInWeeks(Date.now(), new Date(reviewDate));
    const monthsDifference = differenceInMonths(Date.now(), new Date(reviewDate));
    const yearsDifference = differenceInYears(Date.now(), new Date(reviewDate));

    let reviewDuration;
    let reviewSuffixText;
    if (yearsDifference == 0 && monthsDifference == 0 && weeksDifference == 0) {
      reviewDuration = daysDifference;
      reviewSuffixText = daysDifference == 1 ? 'day' : 'days';
    } else if (yearsDifference == 0 && monthsDifference < 1) {
      reviewDuration = weeksDifference;
      reviewSuffixText = weeksDifference == 1 ? 'week' : 'weeks';
    } else if (monthsDifference == 1 || monthsDifference < 12) {
      reviewDuration = monthsDifference;
      reviewSuffixText = monthsDifference == 1 ? 'month' : 'months';
    } else {
      reviewDuration = yearsDifference;
      reviewSuffixText = yearsDifference == 1 ? 'year' : 'years';
    }
    return {
      reviewDuration,
      reviewSuffixText,
    };
  };

  function getDateDiff(fromDate, toDate) {
    const yearDiff = toDate.getFullYear() - fromDate.getFullYear();
    const monthDiff = toDate.getMonth() - fromDate.getMonth();
    const daysDiff = toDate.getDate() - fromDate.getDate();

    const totalMonths = yearDiff * 12 + monthDiff;
    const totalDays = daysDiff;

    return { totalMonths, totalDays };
  }
  function addedMonthOrDaysAgoString(fromDate) {
    const dateDiff = getDateDiff(new Date(fromDate), new Date());
    if (dateDiff.totalMonths > 0) {
      return getStr('extra.pdp.Added', [dateDiff.totalMonths]);
    }
    return getStr('extra.pdp.days.Added', [dateDiff.totalDays]);
  }
  $: reviewSubHeading = !$isReviewAvailable
    ? 'extra.product.first.review.text'
    : isExpanded
      ? 'extra.pdp.peopleSay'
      : 'extra.pdp.learnAboutProduct';
  $: reviewImageStrip = data.flatMap(item => getReviewImages(item));

  function openReviewPreview(images, index = 0) {
    reviewGalleryState.set({
      open: true,
      images,
      activeIndex: index,
    });
  }
</script>

<svelte:window on:keydown={onkeydown} bind:innerWidth />

{#if testFreakData && testFreakData !== null}
  <section class="reviewSection" id="reviews_section" class:isGeekInfoPD={isGeekInfolink}>
    {#if !isGeekInfolink}
      <div class="reviewSection-header">
        <div class="reviewSection-header-left">
          <span class="reviewSection-header-left-title">
            <Text key="extra.pdp.reviews" />
          </span>
          <Text key={reviewSubHeading} />
        </div>

        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <div class="reviewSection-header-right" on:click={handleExpand}>
          {#if isDesktopView}
            <Text key={isExpanded ? 'extra.pdp.collapse' : 'extra.pdp.expand'} />
          {/if}
          <span class="reviewSection-header-right-icon c_icon_new c_icon_new--chevron_{isExpanded ? 'up' : 'down'}" />
        </div>
      </div>
    {/if}
    {#if isExpanded || isGeekInfolink}
      <div class="reviewSection-tabs-content-wrapper" class:isGeekInfo={isGeekInfolink}>
        <div class="reviewSection-tabs">
          <div class="reviewSection-tabs-div">
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <div
              class="reviewSection-tabs-item"
              class:SelectedTab={isSelectedTab == 1}
              on:click={() => handleTabSelection(1)}
            >
              <Text key="extra.pdp.Customer_reviews" />
            </div>
          </div>
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <div class="reviewSection-tabs-item_div">
            <div
              class="reviewSection-tabs-item"
              class:SelectedTab={isSelectedTab == 2}
              on:click={() => handleTabSelection(2)}
            >
              <Text key="extra.pdp.Expert_Reviews" />
            </div>
          </div>
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <div class="reviewSection-tabs-item_div">
            <div
              class="reviewSection-tabs-item"
              class:SelectedTab={isSelectedTab == 3}
              on:click={() => handleTabSelection(3)}
            >
              <Text key="extra.pdp.eXtra_Reviews" />
            </div>
          </div>
        </div>

        <!-- <div class="reviewSection-content"> -->
        <div class="reviewSection-content-header">
          {#if isDesktopView}
            <div class="reviewSection-content-header-left" class:isGeekInfo={isGeekInfolink}>
              <span class="reviewSection-tabs-content-header-left-title review-section-header-title">
                {#if TotalCount > 0}
                  <Text key="extra.pdp.review.ShowingOf" args={[itemToShow, TotalCount, showingOfReviewText]} />
                {:else}
                  <Text key="extra.product.first.review.text" />
                {/if}
              </span>
            </div>
          {/if}
          <div class="reviewSection-content-header-right-btn">
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <div class="reviewSection-content-header-right-btn-text" on:click={() => dispatch('openWriteReview')}>
              <Text key="extra.pdp.Write_review" />
            </div>
          </div>
        </div>
      </div>

      {#if data && data.length > 0}
        {#if reviewImageStrip.length > 0}
          <div class="customer-review-image-strip-wrapper">
            <div class="customer-review-image-strip-title">
              {#if isSelectedTab === 1}
                <Text key="extra.pdp.customer.review.with.images" />
              {:else if isSelectedTab === 2}
                <Text key="extra.pdp.expert.review.with.images" />
              {:else}
                <Text key="extra.pdp.extra.review.with.images" />
              {/if}
            </div>
            <div class="customer-review-image-strip image-strip-scrollable">
              {#each reviewImageStrip as image, index}
                <button
                  type="button"
                  class="customer-review-image-strip-item"
                  aria-label={`Open review image ${index + 1}`}
                  on:click={() => openReviewPreview(reviewImageStrip, index)}
                >
                  <img src={image} alt={`Review preview ${index + 1}`} />
                </button>
              {/each}
            </div>
          </div>
        {/if}
        <div class="reviewSection-content-body">
          {#each data as item, i}
            {@const reviewImages = getReviewImages(item, i)}
            <div class="reviewSection-content-body-wrapper">
              <div class="reviewSection-content-body-wrapper-item">
                {#if item.score}
                  <div>
                    <span class="reviewSection-content-body-wrapper-item-icon">
                      <Icon glyph="icon-star" mobileSize={15} desktopSize={18} type="primary" />

                      <span class="reviewSection-content-body-wrapper-item-rating" class:rtl={isRTL}>
                        {item.score}
                        <span class="reviewSection-content-body-wrapper-item-rating-outof">
                          /{item.score_max}
                        </span>
                      </span>
                    </span>
                  </div>
                  {#if item?.title}
                    <div class="reviewSection-content-body-wrapper-item-dot">
                      <span> .</span>
                    </div>
                  {/if}
                {/if}
                {#if item?.title}
                  <span class="reviewSection-content-body-wrapper-item-header-text">{item?.title}</span>
                {/if}
              </div>
              {#if item?.extract && item?.extract.length > 0}
                <div class="reviewSection-content-body-wrapper-item">
                  <span class="reviewSection-content-body-wrapper-item-text">
                    {#if item?.extract.length >= defaultCharLn}
                      {#if userReviewExpanIndex !== i}
                        {item.extract.slice(0, defaultCharLn)}...
                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                        <span
                          class="reviewSection-content-body-wrapper-item-text-link"
                          on:click={() => userReviewReadMore(i)}
                        >
                          <Text key="extra.pdp.readMore" />
                        </span>
                      {:else}
                        {item?.extract}
                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                        <span
                          class="reviewSection-content-body-wrapper-item-text-link"
                          on:click={() => userReviewReadMore(i)}><Text key="extra.pdp.showLess" /></span
                        >
                      {/if}
                    {:else}{item?.extract}
                    {/if}
                  </span>
                </div>
              {/if}

              {#if item?.pros && item?.pros.length > 0}
                <div class="reviewSection-content-body-wrapper-item">
                  <div>
                    <span class="c_icon_new c_icon_new--check reviewSection-content-body-wrapper-item-icon" />
                  </div>
                  <span class="reviewSection-content-body-wrapper-item-text {openPropsReadMore ? 'propsReadMore' : ''}">
                    {#if item?.pros.length >= defaultCharLn}
                      {#if propsExpanIndex !== i}
                        {item?.pros.slice(0, defaultCharLn)}...
                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                        <span
                          class="reviewSection-content-body-wrapper-item-text-link"
                          on:click={() => propsReadMore(i)}><Text key="extra.pdp.readMore" /></span
                        >
                      {:else}
                        {item?.pros}
                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                        <span
                          class="reviewSection-content-body-wrapper-item-text-link"
                          on:click={() => propsReadMore(i)}><Text key="extra.pdp.showLess" /></span
                        >
                      {/if}
                    {:else}{item?.pros}
                    {/if}
                  </span>
                </div>
              {/if}
              {#if item?.cons && item?.cons.length > 0}
                <div class="reviewSection-content-body-wrapper-item">
                  <div>
                    <span class="c_icon_new c_icon_new--close reviewSection-content-body-wrapper-item-icon" />
                  </div>

                  <span class="reviewSection-content-body-wrapper-item-text {openConsReadMore ? 'consReadMore' : ''}"
                    >{item?.cons.slice(0, defaultCharLn)}
                    {#if item?.cons.length >= defaultCharLn}
                      {#if consExpanIndex !== i}
                        {item?.cons.slice(0, defaultCharLn)}
                        ...
                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                        <span class="reviewSection-content-body-wrapper-item-text-link" on:click={() => consReadMore(i)}
                          ><Text key="extra.pdp.readMore" /></span
                        >
                      {:else}
                        {item?.cons}
                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                        <span class="reviewSection-content-body-wrapper-item-text-link" on:click={() => consReadMore(i)}
                          ><Text key="extra.pdp.showLess" /></span
                        >
                      {/if}
                    {:else}{item?.cons}
                    {/if}
                  </span>
                </div>
              {/if}

              {#if reviewImages.length > 0}
                <div class="reviewSection-content-body-wrapper-item reviewSection-content-body-wrapper-item-images">
                  <div class="review-card-image-strip image-strip-scrollable">
                    {#each reviewImages as image, imageIndex}
                      <button
                        type="button"
                        class="review-card-image-item"
                        aria-label={`Open review image ${imageIndex + 1}`}
                        on:click={() => openReviewPreview(reviewImages, imageIndex)}
                      >
                        <img src={image} alt={`Review image ${imageIndex + 1}`} />
                      </button>
                    {/each}
                  </div>
                </div>
              {/if}
              <div class="reviewSection-content-body-wrapper-item">
                <div class="reviewSection-content-body-wrapper-item-line" />
              </div>
              <div class="reviewSection-content-body-wrapper-item">
                <div class="reviewSection-content-body-wrapper-item-last">
                  <div class="reviewSection-content-body-wrapper-item-last-user">
                    {#if item?.author}
                      <span class="reviewSection-content-body-wrapper-item-last-user-text1">{item?.author}</span>
                    {/if}

                    <span class="reviewSection-content-body-wrapper-item-last-user-icon"
                      ><Icon glyph="icon-verified" mobileSize={14} desktopSize={14} type="primary" /></span
                    >
                  </div>
                  <span class="reviewSection-content-body-wrapper-item-last-user-text2">
                    {addedMonthOrDaysAgoString(item.date)}
                    <span>
                      <Text key="extra.pdp.review.monthago" args={[reviewdurationPeriod(item.date).reviewDuration]} />
                      <Text key="extra.pdp.reviewed.{reviewdurationPeriod(item.date).reviewSuffixText}" />
                    </span>
                  </span>
                </div>
              </div>
            </div>
          {/each}
        </div>
        {#if viewMore}
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <div class="reviewSection-viewmore" on:click={handleViewMore}>
            <Text key="extra.pdp.View_more" />
            <span class="c_icon_new c_icon_new--chevron_down reviewSection-viewmore-icon" />
          </div>
        {/if}
      {/if}
    {/if}
  </section>
  {#if isMobileView}
    <PdpDivider />
  {/if}
{/if}

<style lang="scss">
  @import '../../common/styles/colors.new.scss';

  :target {
    scroll-margin-top: 130px;
  }
  .reviewSection {
    width: 100%;
    padding: 40px 16px;

    @include desktop-min-width {
      padding: 40px 48px 76px 48px;
    }

    &-tabs-content-wrapper {
      display: flex;
      flex-direction: column-reverse;
      gap: 40px;
      margin-bottom: 29px;
      @include desktop-min-width {
        flex-direction: column;
        gap: 16px;
        margin-bottom: 20px;
      }
      &.isGeekInfo {
        @include desktop-min-width {
          flex-direction: column-reverse;
        }
      }
    }

    &-header {
      display: flex;
      justify-content: space-between;
      font-weight: 400;
      font-size: 14px;
      line-height: 16px;

      @include desktop-min-width {
        margin-bottom: 24px;
        font-size: 18px;
        line-height: 20px;
      }

      &-right {
        display: flex;
        align-items: center;
        color: $icon-only-icon-blue;
        cursor: pointer;
        font-weight: 500;
        font-size: 24px;
        line-height: 26px;
        &-icon {
          margin-left: 10px;
          font-size: 20px;
          color: $icon-only-icon-blue;
        }
      }
      &-left {
        color: $neutral-dark-02;
        display: flex;
        gap: 8px;
        flex-direction: column;
        &-title {
          font-size: 20px;
          line-height: 22px;
          color: $neutral-dark-01;
          font-weight: 700;

          @include desktop-min-width {
            font-size: 36px;
            line-height: 40px;
          }
        }
      }
    }

    &-tabs {
      display: flex;
      flex-direction: row;
      align-items: center;
      font-size: 14px;
      line-height: 16px;
      font-weight: 400;
      gap: 18px;
      overflow: auto;
      border-bottom: 1px solid $secondary-gray-02;

      @include desktop-min-width {
        font-size: 16px;
        gap: 48px;
        line-height: 18px;
        overflow: hidden;
        padding: 0 46px;
      }

      .SelectedTab {
        border-bottom: 2px solid $primary-blue;
        color: $primary-blue;
        font-weight: 700;
      }

      &-item {
        padding: 20px 1px;
        height: 56px;
        cursor: pointer;
        @include desktop-min-width {
          height: 50px;
          padding: 16px 0;
        }
      }
    }
    &-content {
      &-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        font-weight: 400;
        color: $neutral-dark-01;
        @include desktop-min-width {
          font-weight: 500;
          font-size: 18px;
          line-height: 20px;
        }

        &-left {
          &-title {
            color: $neutral-dark-02;
          }
          &.isGeekInfo {
            font-weight: 700;
            font-size: 36px;
            line-height: 40px;
          }
        }
        &-right {
          display: flex;
          align-items: center;
          color: $neutral-dark-02;
          flex-direction: column;
          width: 100%;
          gap: 14px;
          @include desktop-min-width {
            flex-direction: row;
            justify-content: flex-end;
            gap: 24px;
            margin: 16.5px 0;
          }
          &-left {
            font-size: 11px;
            line-height: 12px;
            color: $neutral-dark-02;
            @include desktop-min-width {
              font-size: 18px;
              line-height: 20px;
            }
          }

          &-img {
            height: 24px;
            margin-left: 6px;
          }
          &-btn {
            background-color: $primary-blue;
            border: 1px solid $primary-blue;
            border-radius: 50px;
            cursor: pointer;
            width: 100%;
            display: flex;
            width: 100%;
            justify-content: center;
            margin-top: 18px;

            @include desktop-min-width {
              margin-top: 0;
              width: fit-content;
            }

            &-text {
              width: 171px;
              padding: 15px 36px;
              font-size: 16px;
              line-height: 18px;
              font-weight: 700;
              letter-spacing: 0;
              color: $primary-white;
            }
          }
        }
      }

      &-body {
        display: flex;
        flex-direction: column;
        border-radius: 12px;

        @include desktop-min-width {
          // margin-right: 390px;
          border-radius: 8px;
        }

        &-wrapper {
          border: 1px solid $neutral-dark-05;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 16px;
          border-radius: 12px;
          @include desktop-min-width {
            border-radius: 8px;
            padding: 24px;
            gap: 16px;
          }

          &-item {
            display: flex;
            gap: 8px;
            font-size: 14px;
            line-height: 16px;
            font-weight: 400;
            &.reviewSection-content-body-wrapper-item-images {
              margin-left: 35px;
            }
            @include desktop-min-width {
              gap: 14px;
            }
            &-text-link {
              color: $primary-blue;
              cursor: pointer;
              font-weight: 500;
            }

            &-icon {
              font-size: 20px;
              display: flex;
              align-items: center;
              width: max-content;
            }
            &-header-text {
              font-weight: 500;
              font-size: 20px;
              line-height: 22px;
              @include desktop-min-width {
                font-size: 24px;
                line-height: 26px;
              }
            }
            &-rating {
              font-weight: 400;
              font-size: 20px;
              line-height: 22px;
              color: $neutral-dark-01;
              margin-left: 9px;
              display: inline-flex;
              &.rtl {
                direction: rtl;
              }
              @include desktop-min-width {
                font-size: 24px;
                line-height: 26px;
              }
              &-outof {
                font-weight: 500;
                color: $secondary-gray-02;
              }
            }
            &-dot {
              font-weight: 400;
              font-size: 20px;
              line-height: 22px;
              color: $neutral-dark-03;
              position: relative;
              top: -5px;
              @include desktop-min-width {
                font-size: 24px;
                line-height: 26px;
              }
            }
            &-line {
              height: 1px;
              background-color: $secondary-gray-02;
              width: 100%;
            }
            &-last {
              display: flex;
              flex-direction: column;
              gap: 4px;
              &-user {
                display: flex;
                gap: 6px;

                &-icon {
                  font: 16px;
                  color: $color-yellow;
                }
                &-text1 {
                  font-weight: 700;
                  font-size: 16px;
                  line-height: 18px;
                }
                &-text2 {
                  font-weight: 500;
                  font-size: 11px;
                  line-height: 12px;
                  color: $neutral-dark-03;
                }
              }
            }
          }
        }
      }
    }

    .customer-review-image-strip-wrapper {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 20px 0;
    }

    .customer-review-image-strip-title {
      color: $neutral-dark-01;
      font-size: 18px;
      line-height: 22px;
      font-weight: 700;
    }

    .customer-review-image-strip,
    .review-card-image-strip {
      display: flex;
      gap: 16px;
      flex-wrap: nowrap;
    }

    .image-strip-scrollable {
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none;
      scroll-behavior: smooth;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    .customer-review-image-strip-item,
    .review-card-image-item {
      width: 78px;
      height: 78px;
      flex: 0 0 78px;
      border: none;
      border-radius: 8px;
      overflow: hidden;
      padding: 0;
      background: $neutral-light-04;
      cursor: pointer;

      @include desktop-min-width {
        width: 88px;
        height: 88px;
        flex-basis: 88px;
      }

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
    }

    &-viewmore {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      padding: 12px 36px;
      gap: 4px;
      border: 1px solid $primary-blue;
      border-radius: 100px;
      font-weight: 700;
      font-size: 16px;
      line-height: 18px;
      color: $primary-blue;
      margin: 15px 0 30px;

      @include desktop-min-width {
        width: fit-content;
      }

      &-icon {
        font-size: 20px;
      }
    }
  }

  .isGeekInfoPD {
    padding: 16px;
    @include desktop-min-width {
      padding: 36.5px 0 0;
    }
  }

  :global(.reviewSection-content-body-wrapper-item .c_icon_new.c_icon_new--check) {
    color: $functional-green-01;
  }
  :global(.reviewSection-content-body-wrapper-item .c_icon_new.c_icon_new--close) {
    color: $functional-orange;
  }
</style>
